// ... imports
// (removed duplicate import)
// (removed duplicate Icon import)

// --- Restored Old CardDetails UI, centered modal, stack-like ---
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
// (removed unused RoleBasedHeader import)
import Icon from '../../components/AppIcon';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ActivityTimeline from '../../components/ui/ActivityTimeline';
import useToast from '../../hooks/useToast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import teamService from '../../utils/teamService';
import { getAssignableMembers } from '../../utils/rolePermissions';

import sessionService from '../../utils/sessionService.js';
import { useUserProfile } from '../../hooks/useUserProfile';
import { getProgressFromStatus } from '../../utils/taskProgress';
// (removed unused authService and sessionService imports)

const CardDetails = ({ onSave }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const cardId = searchParams.get('id') || searchParams.get('cardId');
  const { showToast, hideToast } = useToast();

  // Validate card ID format
  const isValidUUID = (str) => {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  // Authentication state
  // Removed unused currentUser, userRole, currentOrganization state

  const { userProfile } = useUserProfile();
  const resolvedRole = (userProfile?.role || sessionService.getUserRole() || '').toLowerCase();
  const currentUserId = sessionService.getCurrentUser?.()?.id;

  const [cardData, setCardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingChanges, setPendingChanges] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [members, setMembers] = useState([]);

  // Detect mode
  const mode = searchParams.get('mode') || 'view';
  const isCreationMode = mode === 'create';
  const initialColumnId = searchParams.get('columnId');
  const initialProjectId = searchParams.get('projectId');

  // Derive permissions from the most current card data
  const isAssignedToMe = React.useMemo(() => {
    const targetCard = cardData || location.state?.card;
    if (!targetCard || !currentUserId) return false;
    
    // Check assignments array (handles both object array and ID array formats)
    const assignments = targetCard.assigned_to || 
                      targetCard.assignees || 
                      targetCard.assignments || 
                      [];
    return assignments.some(a => {
      // Handle complex object (a.user_id or a.id) or simple ID string (a)
      const memberId = typeof a === 'object' ? (a.user_id || a.id) : a;
      return memberId === currentUserId;
    });
  }, [cardData, location.state?.card, currentUserId]);

  // Permission logic: owners/admins can always edit, others can edit if assigned
  const canEdit = ['owner', 'admin'].includes(resolvedRole) || isAssignedToMe;
  const canDelete = ['owner','admin'].includes(resolvedRole);
  const canComment = true; // All roles can comment now that viewer is removed

  // Cleanup function to fix invalid IDs in existing checklist items
  const cleanupInvalidIds = (checklistItems) => {
    if (!checklistItems || checklistItems.length === 0) {
      return checklistItems;
    }

    console.log('🔧 Checking checklist items for invalid IDs:', checklistItems.length, 'items');

    return checklistItems.map(item => {
      // Check if this item has an invalid ID (old ai- prefix or too short)
      if (item.id && (item.id.startsWith('ai-') || item.id.length < 32)) {
        const newId = `temp-ai-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
        console.log('🔧 Fixing invalid ID:', item.id, '→', newId, 'for item:', item.text);
        return {
          ...item,
          id: newId
        };
      }
      return item;
    });
  };

  /**
   * Normalizes card data to ensure consistent field names regardless of source
   * (e.g., from navigation state, fresh poll, or different API services)
   */
  const normalizeCardData = (rawCard) => {
    if (!rawCard) return null;

    console.log('🔧 Normalizing card data:', rawCard.id || 'new');

    const checklist = cleanupInvalidIds(
      rawCard.checklist_items || rawCard.checklist || []
    );

    // Normalize assignees to a flat array of IDs for the Select component
    let assignedTo = [];
    const assignments = rawCard.assigned_to || rawCard.assignments || rawCard.assignees || [];
    
    if (Array.isArray(assignments)) {
      assignedTo = assignments.map(a => {
        if (typeof a === 'object' && a !== null) {
          return a.user_id || a.id;
        }
        return a;
      }).filter(Boolean);
    }

    // Improve project_id resolution: check root, then nested project object
    const projectId = rawCard.project_id || rawCard.projectId || (rawCard.project && (rawCard.project.id || rawCard.project.card_project_id));

    return {
      ...rawCard,
      // Field Mapping: snake_case (form expectation) <- camelCase (navigation/service fallback)
      column_id: rawCard.column_id || rawCard.columnId,
      project_id: projectId,
      start_date: rawCard.start_date || rawCard.startDate,
      due_date: rawCard.due_date || rawCard.dueDate,
      assigned_to: assignedTo,
      checklist: checklist,
      // Retain formatted dates for raw display if needed
      display_due_date: rawCard.due_date || rawCard.dueDate,
    };
  };

  useEffect(() => {
    let didCancel = false;
    setLoadError(null);
    
    const loadCardData = async () => {
      // Handle Creation Mode
      if (isCreationMode) {
        const defaultCardData = {
          title: '',
          description: '',
          priority: 'medium',
          column_id: initialColumnId,
          project_id: initialProjectId,
          assigned_to: [],
          labels: [],
          checklist: [],
          start_date: null,
          due_date: null
        };
        if (!didCancel) {
          setCardData(defaultCardData);
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      
      // 1. Initial Load: Try location state first for instant UI
      if (location.state?.card) {
        console.log('🚀 Using cached card data from navigation state');
        const normalizedCardData = normalizeCardData(location.state.card);
        if (!didCancel) {
          setCardData(normalizedCardData);
          // If we have cached data, don't show the initial loading spinner
          setIsLoading(false);
        }
      } else if (cardId) {
        // Only set loading true if we don't have cached data but need to fetch
        setIsLoading(true);
      }

      // 2. Fresh Fetch: Always fetch from API if we have a valid cardId
      if (cardId) {
        if (!isValidUUID(cardId)) {
          if (!didCancel) {
            setLoadError('Invalid card ID format.');
            setIsLoading(false);
          }
          return;
        }

        try {
          const apiService = (await import('../../utils/realApiService')).default;
          console.log(`📡 Fetching fresh card data for ID: ${cardId}`);
          const result = await apiService.cards.getById(cardId);
          
          if (result && result.data) {
            console.log('✅ Fresh card data received:', result.data.id);
            const normalizedCardData = normalizeCardData(result.data);
            if (!didCancel) {
              setCardData(normalizedCardData);
            }
          } else {
            console.warn('⚠️ No card data returned from API for ID:', cardId);
            if (!location.state?.card) {
               throw new Error('No card data found');
            }
          }
        } catch (error) {
          console.error('❌ Failed to fetch fresh card data:', error);
          if (!location.state?.card && !didCancel) {
            setLoadError(error.message || 'Failed to load card');
          }
        } finally {
          if (!didCancel) {
            setIsLoading(false);
          }
        }
      }
    };
    loadCardData();
    return () => {
      didCancel = true;
    };
  }, [cardId, isCreationMode, initialColumnId, initialProjectId, location.state, retryCount]);

  // Load columns for status selection
  const [columns, setColumns] = useState([]);
  useEffect(() => {
    const loadColumns = async () => {
      const projectId = searchParams.get('projectId') || cardData?.project_id;
      if (!projectId) return;

      try {
        const kanbanApi = (await import('../../utils/kanbanApiService')).default;
        const boardResponse = await kanbanApi.board.getOrCreateForProject(projectId);
        const boardData = boardResponse.data || boardResponse;
        
        if (boardData?.columns) {
          const mappedColumns = boardData.columns.map(col => ({
            id: col.id,
            title: col.title || col.name || 'Untitled'
          }));
          setColumns(mappedColumns);

          // Ensure cardData has the correct column title for progress calculation
          setCardData(prev => {
            if (prev && !prev.columnTitle) {
              const currentColumn = mappedColumns.find(c => c.id === (prev.column_id || prev.columnId));
              if (currentColumn) {
                return { ...prev, columnTitle: currentColumn.title };
              }
            }
            return prev;
          });
        }
      } catch (err) {
        console.error('Failed to load columns for status selector:', err);
      }
    };
    if (cardData) loadColumns();
  }, [cardData?.project_id, cardId, isCreationMode]);

  // Load project members
  useEffect(() => {
    const loadMembers = async () => {
      const orgId = sessionService.getCurrentOrganization()?.id;
      if (!orgId) return;

      try {
        const teamMembers = await teamService.getTeamMembers(orgId);
        if (teamMembers && Array.isArray(teamMembers)) {
          setMembers(teamMembers);
        }
      } catch (error) {
        console.error('Failed to load team members:', error);
      }
    };
    loadMembers();
  }, []);

  const handleRetry = () => setRetryCount((c) => c + 1);

  // Track changes
  const trackChange = (field, value) => {
    const updatedData = { [field]: value };
    setCardData((prev) => ({ ...prev, ...updatedData }));
    setPendingChanges((prev) => ({ ...prev, ...updatedData }));
    setHasUnsavedChanges(true);
  };
  const hasFieldChanged = (field) => pendingChanges.hasOwnProperty(field);
  const handleTitleChange = (newTitle) => trackChange('title', newTitle);
  const handleDescriptionChange = (newDescription) =>
    trackChange('description', newDescription);
  const handleMembersChange = (newUserIds) => trackChange('assigned_to', newUserIds);
  const handleDueDateChange = (newDueDate) => {
    // Validate: due_date must be >= start_date
    if (newDueDate && cardData.start_date) {
      if (new Date(newDueDate) < new Date(cardData.start_date)) {
        showToast('Due date cannot be earlier than start date', 'error');
        return;
      }
    }
    trackChange('due_date', newDueDate);
  };
  const handleStartDateChange = (newStartDate) => {
    // Validate: start_date must be <= due_date
    if (newStartDate && cardData.due_date) {
      if (new Date(cardData.due_date) < new Date(newStartDate)) {
        showToast('Start date cannot be later than due date', 'error');
        return;
      }
    }
    trackChange('start_date', newStartDate);
  };
  const handleLabelsChange = (newLabels) => trackChange('labels', newLabels);
  const handleChecklistChange = (newChecklist) =>
    trackChange('checklist', newChecklist);
  const handleStatusChange = (newColumnId) => {
    const selectedColumn = columns.find(c => c.id === newColumnId);
    if (selectedColumn) {
      // Validation: Check checklist completion before marking as Done
      // Owners can bypass this rule
      if (resolvedRole !== 'owner') {
        const statusName = (selectedColumn.title || selectedColumn.name || '').toLowerCase();
        const isCompletedStatus = statusName.includes('done') || statusName.includes('complete');

        if (isCompletedStatus && cardData.checklist && cardData.checklist.length > 0) {
          const hasIncompleteItems = cardData.checklist.some(item => !item.completed);
          if (hasIncompleteItems) {
            showToast('You cannot mark this task as done until all checklist items are completed.', 'error');
            return;
          }
        }
      }

      setCardData(prev => ({ ...prev, column_id: newColumnId, columnTitle: selectedColumn.title }));
      setPendingChanges(prev => ({ ...prev, column_id: newColumnId }));
      setHasUnsavedChanges(true);
    }
  };

  // Helper function to save checklist changes via dedicated endpoints
  const saveChecklistChanges = async (cardId, pendingChanges) => {
    if (!pendingChanges.checklist) {
      return; // No checklist changes to save
    }

    const realApiService = (await import('../../utils/realApiService')).default;
    const newChecklist = pendingChanges.checklist;
    const originalChecklist = cardData.checklist || [];

    console.log('🔧 Saving checklist changes for card:', cardId);
    console.log('🔧 Original checklist:', originalChecklist);
    console.log('🔧 New checklist:', newChecklist);

    // Separate items into new, updated, and deleted
    const newItems = [];
    const updatedItems = [];
    const existingIds = new Set();

    // Process new checklist items
    for (const item of newChecklist) {
      // Check if this is a temporary ID (includes old 'ai-' prefix and new 'temp-' prefix)
      const isTemporaryId = !item.id ||
                           item.id.startsWith('temp-') ||
                           item.id.startsWith('ai-') ||
                           (item.id && item.id.length < 32); // Invalid UUID length

      if (isTemporaryId) {
        // New item (no id, temporary id, or invalid old AI id)
        console.log('🆕 Creating new item:', item.text, 'with temp ID:', item.id);
        newItems.push({
          text: item.text,
          position: item.position || 0,
          completed: item.completed || false,
          ai_generated: item.aiGenerated || false,
          confidence: item.confidence || null,
          metadata: item.metadata || null
        });
      } else {
        // Existing item to update (has a real UUID from database)
        console.log('🔄 Updating existing item:', item.text, 'with ID:', item.id);
        existingIds.add(item.id);
        updatedItems.push({
          id: item.id,
          text: item.text,
          position: item.position || 0,
          completed: item.completed || false
        });
      }
    }

    // Find deleted items (existed in original but not in new)
    // Only include items with real database IDs (not temporary IDs)
    const deletedItems = originalChecklist.filter(item => {
      if (!item.id || existingIds.has(item.id)) {
        return false; // Item still exists or has no ID
      }

      // Check if this is a temporary ID that should not be deleted via API
      const isTemporaryId = item.id.startsWith('temp-') ||
                           item.id.startsWith('ai-') ||
                           item.id.length < 32; // Invalid UUID length

      return !isTemporaryId; // Only delete items with real database IDs
    });

    console.log('🔧 New items to create:', newItems);
    console.log('🔧 Items to update:', updatedItems);
    console.log('🔧 Items to delete:', deletedItems);

    try {
      // Create new items in bulk
      if (newItems.length > 0) {
        console.log('🔧 Creating new checklist items...');
        await realApiService.checklist.createBulk(cardId, { items: newItems });
      }

      // Update existing items one by one
      for (const item of updatedItems) {
        console.log('🔧 Updating checklist item:', item.id);
        await realApiService.checklist.updateItem(item.id, {
          text: item.text,
          completed: item.completed,
          position: item.position
        });
      }

      // Delete removed items
      for (const item of deletedItems) {
        console.log('🔧 Deleting checklist item:', item.id);
        await realApiService.checklist.deleteItem(item.id);
      }

      // Fetch the latest checklist from backend to get new IDs
      console.log('🔧 Fetching updated checklist from backend...');
      const updatedChecklistResponse = await realApiService.checklist.getByCard(cardId);
      const updatedChecklist = updatedChecklistResponse.data || updatedChecklistResponse;

      // Update local state with fresh data from backend
      setCardData(prev => ({
        ...prev,
        checklist: updatedChecklist
      }));

      console.log('✅ Checklist changes saved successfully');
    } catch (error) {
      console.error('❌ Error saving checklist changes:', error);
      throw error; // Re-throw to be handled by the main save function
    }
  };

  // Save changes and update board
  const handleSaveChanges = async () => {
    if (!hasUnsavedChanges || !cardData) return;
    setIsSaving(true);
    try {
      const kanbanApi = (await import('../../utils/kanbanApiService')).default;
      let targetCardId = cardData.id;

      // Handle Creation
      if (isCreationMode) {
        if (!cardData.title?.trim()) {
          showToast('Please enter a title for the task.', 'warning');
          setIsSaving(false);
          return;
        }

        const createPayload = {
          title: cardData.title,
          description: cardData.description || '',
          priority: cardData.priority || 'medium',
          column_id: initialColumnId || cardData.column_id,
          start_date: cardData.start_date,
          due_date: cardData.due_date,
          assigned_to: cardData.assigned_to || [],
          labels: cardData.labels || []
        };

        console.log('🚀 Creating new card:', createPayload);
        const result = await kanbanApi.cards.create(createPayload.column_id, createPayload);
        
        // Extract card ID - backend might return { data: card } or the card object directly
        const newCard = result?.data || result;
        if (newCard && newCard.id) {
          targetCardId = newCard.id;
          console.log('✅ Card created successfully with ID:', targetCardId);
        } else {
          console.error('❌ Unexpected card creation response:', result);
          throw new Error('Failed to create card: Invalid response format');
        }

        // Save checklist separately if needed
        if (cardData.checklist && cardData.checklist.length > 0) {
          console.log('🔧 Saving checklist for new card...');
          await saveChecklistChanges(targetCardId, { checklist: cardData.checklist });
        }
      } else if (cardData.id && Object.keys(pendingChanges).length > 0) {
        // Validate card ID format before making API call
        if (!isValidUUID(cardData.id)) {
          console.warn(
            'Invalid card ID format, skipping API update:',
            cardData.id
          );
          console.log(
            'Note: Card might be newly created and not yet synced with backend'
          );
          return;
        }

        const kanbanApi = (await import('../../utils/kanbanApiService')).default;
        // Only send fields that the backend accepts for card updates
        const backendSupportedFields = [
          'title',
          'description',
          'priority',
          'position',
          'column_id',
          'start_date',
          'due_date',
          'assigned_to',
          'labels'
        ];

        // Prepare payload for card update (excluding checklist)
        const sanitizedPayload = {};
        for (const [key, value] of Object.entries(pendingChanges)) {
          if (backendSupportedFields.includes(key) && value !== undefined) {
            sanitizedPayload[key] = value;
          }
        }

        // Only make API call if there are supported fields to update
        if (Object.keys(sanitizedPayload).length > 0) {
          console.log('Updating card with supported fields:', sanitizedPayload);
          await kanbanApi.cards.update(cardData.id, sanitizedPayload);
        } else {
          console.log(
            'No backend-supported fields to update, skipping API call'
          );
        }

        // Handle checklist changes via dedicated endpoints
        if (pendingChanges.checklist) {
          console.log('🔧 Processing checklist changes...');
          await saveChecklistChanges(cardData.id, pendingChanges);
        }

        // Handle other unsupported fields
        const unsupportedFields = Object.keys(pendingChanges).filter(
          (key) => !backendSupportedFields.includes(key) && key !== 'checklist'
        );
        if (unsupportedFields.length > 0) {
          console.log(
            'Note: Some fields are not yet supported by the backend API:',
            unsupportedFields
          );
        }
      }
      if (onSave && typeof onSave === 'function') {
        onSave({ ...cardData, ...pendingChanges });
      }
      setPendingChanges({});
      setHasUnsavedChanges(false);

      // Show success notification
      showToast('Card saved successfully! Redirecting to board...', 'success', 2000);

      // Navigate back to kanban board after a short delay
      setTimeout(() => {
        const returnPath = location.state?.returnPath || location.state?.returnTo || '/kanban-board';
        navigate(returnPath, {
          state: {
            // Preserve project context if available
            projectId: location.state?.projectId,
            project: location.state?.project,
            // Add success flag for potential board-level notifications
            cardSaved: true,
            savedCardId: cardData.id,
            savedCardTitle: cardData.title
          }
        });
      }, 1500);

    } catch (error) {
      console.error('Error saving card changes:', error);
      // Show error toast instead of alert
      showToast('Failed to save changes. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };
  const handleDiscardChanges = () => {
    if (!hasUnsavedChanges) return;
    setPendingChanges({});
    setHasUnsavedChanges(false);
    window.location.reload();
  };
  const handleAddComment = (comment) => {};

  // Alias for handleSaveChanges to match component expectations
  const handleSave = handleSaveChanges;

  const handleClose = () => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm(
        'You have unsaved changes. Are you sure you want to leave without saving?'
      );
      if (!confirmLeave) return;
    }
    const returnPath = location.state?.returnPath || '/kanban-board';
    navigate(returnPath, {
      state: {
        // Preserve project context if available
        projectId: location.state?.projectId,
        project: location.state?.project
      }
    });
  };
  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this card?')) {
      try {
        setIsSaving(true);
        const apiService = (await import('../../utils/realApiService')).default;
        console.log(`🗑️ Deleting card ID: ${cardData.id}`);
        await apiService.cards.delete(cardData.id);
        
        // Show success notification for deletion
        showToast('Card deleted successfully!', 'success', 3000);

        // Navigate back with preserved state
        const returnPath = location.state?.returnPath || '/kanban-board';
        navigate(returnPath, {
          state: {
            projectId: location.state?.projectId,
            project: location.state?.project,
            cardDeleted: true,
            deletedCardTitle: cardData.title
          }
        });
      } catch (error) {
        console.error('❌ Failed to delete card:', error);
        showToast('Failed to delete card. Please try again.', 'error');
      } finally {
        setIsSaving(false);
      }
    }
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // --- Options ---
  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
  ];

  const labelOptions = [
    { value: 'bug', label: 'Bug' },
    { value: 'feature', label: 'Feature' },
    { value: 'improvement', label: 'Improvement', color: '#10b981' },
    { value: 'documentation', label: 'Documentation', color: '#f59e0b' },
    { value: 'testing', label: 'Testing', color: '#8b5cf6' },
  ];

  const assigneeOptions = getAssignableMembers(
    members,
    resolvedRole,
    currentUserId
  ).map((member) => ({
    value: member.id,
    label: member.name,
  }));

  const columnOptions = columns.map((column) => ({
    value: column.id,
    label: column.title || column.name || 'Untitled',
  }));

  const [isGeneratingChecklist, setIsGeneratingChecklist] = useState(false);
  const handleAISuggestion = async () => {
    if (!cardData.title?.trim()) {
      showToast('Please enter a card title first.', 'warning');
      return;
    }
    if ((cardData.checklist || []).length > 0) {
      if (!window.confirm('This will replace your current checklist items. Do you want to continue?')) {
        return;
      }
    }

    setIsGeneratingChecklist(true);
    try {
      const { generateAIChecklist } = await import('../../utils/aiChecklistService');
      const result = await generateAIChecklist(
        cardData.title,
        cardData.description,
        cardData.priority,
        'general'
      );

      if (result.success) {
        handleChecklistChange(result.items || []);
        showToast('AI checklist generated!', 'success');
      } else {
        showToast('Failed to generate AI suggestions.', 'error');
      }
    } catch (error) {
      showToast('Failed to generate AI suggestions.', 'error');
    } finally {
      setIsGeneratingChecklist(false);
    }
  };

  // --- UI ---
  if (isLoading) {
    return (
      <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'>
        <div className='bg-surface rounded-lg shadow-focused w-full max-w-md p-8 flex flex-col items-center border border-border'>
          <div className='animate-spin rounded-full h-12 w-12 border-4 border-muted border-t-primary mb-4'></div>
          <div className='text-lg font-semibold text-text-primary'>Loading Task Details</div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'>
        <div className='bg-surface rounded-lg shadow-focused w-full max-w-md p-8 flex flex-col items-center border border-error/50'>
          <Icon name='AlertTriangle' size={48} className='text-error mb-4' />
          <div className='text-lg font-semibold text-text-primary mb-4'>Failed to Load Task</div>
          <p className='text-text-secondary text-center mb-6'>{loadError}</p>
          <div className='flex gap-3 w-full'>
            <Button onClick={handleRetry} className='flex-1'>Retry</Button>
            <Button variant='outline' onClick={handleClose} className='flex-1'>Back to Board</Button>
          </div>
        </div>
      </div>
    );
  }

  if (!cardData) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm'>
      <div className='relative bg-surface rounded-lg shadow-focused w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col'>
        
        {/* Header */}
        <div className='flex items-center justify-between p-6 border-b border-border'>
          <div className="flex flex-col">
            <Breadcrumb projectName={location.state?.project?.name || cardData?.project?.name || 'Project'} />
            <div className="flex items-center gap-3 mt-1">
              <h2 className='text-xl font-bold text-text-primary'>
                {isCreationMode ? 'New Task' : 'Task Details'}
              </h2>
            </div>
          </div>
          <Button variant='ghost' size='icon' onClick={handleClose}>
            <Icon name='X' size={24} />
          </Button>
        </div>

        {/* Scrollable Content */}
        <div className='flex-1 overflow-y-auto p-6 space-y-6'>
          {/* Title */}
          <Input
            label='Task Title'
            type='text'
            placeholder='Enter task title...'
            value={cardData.title || ''}
            onChange={(e) => handleTitleChange(e.target.value)}
            required
            disabled={!canEdit}
          />

          {/* AI Suggestions Button */}
          {canEdit && (
            <div className='flex justify-end -mt-2'>
              <button
                type='button'
                onClick={handleAISuggestion}
                disabled={isGeneratingChecklist || !cardData.title?.trim()}
                className='flex items-center space-x-2 px-3 py-1 text-xs bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full hover:from-purple-600 hover:to-blue-600 disabled:opacity-50 transition-all shadow-sm'
              >
                {isGeneratingChecklist ? (
                  <><Icon name='Loader2' size={12} className='animate-spin' /><span>Generating...</span></>
                ) : (
                  <><Icon name='Sparkles' size={12} /><span>✨ AI Suggestions</span></>
                )}
              </button>
            </div>
          )}

          {/* Priority & Status */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <Select
              label='Priority'
              options={priorityOptions}
              value={cardData.priority || 'medium'}
              onChange={(val) => trackChange('priority', val)}
              disabled={!canEdit}
            />

            <Select
              label='Status'
              options={columnOptions}
              value={cardData.column_id || ''}
              onChange={handleStatusChange}
              required
              disabled={!canEdit}
            />
          </div>

          {/* Description */}
          <Input
            label='Description'
            type='textarea'
            placeholder='Enter task description...'
            value={cardData.description || ''}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            rows={4}
            disabled={!canEdit}
          />

          {/* Start Date & Due Date */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <Input
              label='Start Date'
              type='date'
              value={cardData.start_date ? cardData.start_date.split('T')[0] : ''}
              onChange={(e) => handleStartDateChange(e.target.value)}
              disabled={!canEdit}
            />

            <Input
              label='Due Date'
              type='date'
              value={cardData.due_date ? cardData.due_date.split('T')[0] : ''}
              onChange={(e) => handleDueDateChange(e.target.value)}
              disabled={!canEdit}
            />
          </div>

          {/* Assignees */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <Select
              label='Assignees'
              options={assigneeOptions}
              value={cardData.assigned_to || []}
              onChange={(val) => handleMembersChange(Array.isArray(val) ? val : [val])}
              multiple
              disabled={!canEdit}
            />
          </div>

          {/* Labels */}
          <Select
            label='Labels'
            options={labelOptions}
            value={cardData.labels || []}
            onChange={(val) => handleLabelsChange(Array.isArray(val) ? val : [val])}
            multiple
            disabled={!canEdit}
          />

          {/* Checklist */}
          <div className='space-y-3'>
            <div className='flex items-center justify-between'>
              <label className='block text-sm font-medium text-text-primary'>Task Checklist</label>
              {canEdit && (cardData.checklist || []).length > 0 && (
                <button
                  type='button'
                  onClick={() => handleChecklistChange([])}
                  className='text-xs text-error hover:underline flex items-center gap-1'
                >
                  <Icon name='Trash2' size={12} />
                  Clear All
                </button>
              )}
            </div>
            <div className='space-y-2 p-4 bg-muted/30 rounded-lg border border-border'>
              {(cardData.checklist || []).map((item, index) => (
                <div key={index} className='flex items-center space-x-3 group'>
                  <input
                    type='checkbox'
                    checked={item.completed || false}
                    onChange={(e) => {
                      if (!canEdit) return;
                      const updated = [...cardData.checklist];
                      updated[index] = { ...item, completed: e.target.checked };
                      handleChecklistChange(updated);
                    }}
                    disabled={!canEdit}
                    className='rounded border-border cursor-pointer h-4 w-4 text-primary focus:ring-primary'
                  />
                  <input
                    type='text'
                    value={item.text}
                    onChange={(e) => {
                      const updated = [...cardData.checklist];
                      updated[index] = { ...item, text: e.target.value };
                      handleChecklistChange(updated);
                    }}
                    disabled={!canEdit}
                    className='text-sm text-text-primary bg-transparent border-none focus:ring-0 flex-1 hover:bg-muted/50 rounded px-2 py-1 -ml-2 transition-colors'
                    placeholder='Item text...'
                  />
                  {canEdit && (
                    <button
                      type='button'
                      onClick={() => {
                        handleChecklistChange(cardData.checklist.filter((_, i) => i !== index));
                      }}
                      className='opacity-0 group-hover:opacity-100 text-text-secondary hover:text-error transition-opacity'
                    >
                      <Icon name='X' size={14} />
                    </button>
                  )}
                </div>
              ))}

              {canEdit && (
                <button
                  type='button'
                  onClick={() => {
                    handleChecklistChange([
                      ...(cardData.checklist || []),
                      { text: '', completed: false, position: (cardData.checklist || []).length }
                    ]);
                  }}
                  className='text-xs text-primary hover:underline flex items-center gap-1 mt-2 font-medium'
                >
                  <Icon name='Plus' size={14} />
                  Add Checklist Item
                </button>
              )}

              {!(cardData.checklist || []).length && (
                <div className='text-sm text-text-secondary text-center py-2 italic'>
                  No checklist items added yet
                </div>
              )}
            </div>
          </div>

          {/* Activity Timeline */}
          {!isCreationMode && (
            <div className="pt-8 border-t border-border mt-8">
              <ActivityTimeline 
                card={cardData} 
                canComment={canComment}
              />
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className='p-6 border-t border-border bg-muted/10 flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            {canDelete && (
              <Button
                variant='danger'
                outline
                onClick={handleDelete}
                disabled={isSaving}
                iconName='Trash2'
              >
                Delete
              </Button>
            )}
          </div>
          
          <div className='flex items-center gap-3'>
            {hasUnsavedChanges && !isCreationMode && (
              <Button
                variant='outline'
                onClick={handleDiscardChanges}
                disabled={isSaving}
              >
                Discard
              </Button>
            )}
            <Button
              variant='outline'
              onClick={handleClose}
            >
              Close
            </Button>
            {(hasUnsavedChanges || isCreationMode) && canEdit && (
              <Button
                variant='primary'
                onClick={handleSaveChanges}
                disabled={isSaving}
                loading={isSaving}
              >
                {isCreationMode ? 'Create Task' : 'Save Changes'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(CardDetails);
